/**
 * Helpers for entry-point and event-property derivation. Pure functions —
 * no React, no PostHog imports. Consumed by `lib/analytics.ts` callers.
 */
/**
 * Derive `entry_kind` from the canonical parsed route. The discriminated
 * union in `RouteEntity` (returned by `useRouteEntity()`, defined in
 * `lib/urls.ts`) is the single source of truth for route shape — same
 * parser that drives every routing decision in the viewer. Adding a
 * new route type elsewhere surfaces here as a TypeScript exhaustiveness
 * error rather than silently bucketing into "other".
 */
export function routeEntityToEntryKind(entity) {
    switch (entity.type) {
        case "home":
            return "stream";
        case "collectionsTab":
            return "collections_tab";
        case "blockDeeplink":
            return "block_link";
        case "standaloneCollection":
        case "collectionBySlug":
            return "collection_link";
        case "entity":
            return "group_link";
        case "name":
            return "profile_link";
        case "transientCollection":
            return "transient";
        case "unknown":
            return "other";
    }
}
const SEARCH_HOSTS = ["google.", "bing.", "duckduckgo.", "yahoo.", "kagi."];
const SOCIAL_HOSTS = [
    "facebook.",
    "instagram.",
    "twitter.",
    "x.com",
    "linkedin.",
    "reddit.",
    "tiktok.",
    "youtube.",
    "t.co",
];
const IMESSAGE_HOSTS = ["messages.apple.com", "imessage."];
export function referrerKindFromUrl(referrer) {
    if (!referrer)
        return "direct";
    let host = "";
    try {
        host = new URL(referrer).hostname.toLowerCase();
    }
    catch {
        return "other";
    }
    if (typeof window !== "undefined" && host === window.location.hostname) {
        return "internal";
    }
    if (IMESSAGE_HOSTS.some((h) => host.includes(h)))
        return "imessage";
    if (host.includes("mail.") || host.endsWith("outlook.com") || host.endsWith("gmail.com")) {
        return "email";
    }
    if (SEARCH_HOSTS.some((h) => host.includes(h)))
        return "search";
    if (SOCIAL_HOSTS.some((h) => host.includes(h)))
        return "social";
    return "other";
}
export function utmParamsFromSearch(search) {
    const params = new URLSearchParams(search);
    const result = {};
    const source = params.get("utm_source");
    const medium = params.get("utm_medium");
    const campaign = params.get("utm_campaign");
    if (source)
        result.utm_source = source;
    if (medium)
        result.utm_medium = medium;
    if (campaign)
        result.utm_campaign = campaign;
    return result;
}
export function linkTypeFromHref(href) {
    let host = "";
    try {
        host = new URL(href).hostname.toLowerCase();
    }
    catch {
        return "other";
    }
    if (host.includes("twitter.com") || host === "x.com" || host.endsWith(".x.com") || host === "t.co") {
        return "twitter";
    }
    if (host.includes("instagram.com"))
        return "instagram";
    if (host.includes("youtube.com") || host === "youtu.be")
        return "youtube";
    if (host.includes("tiktok.com"))
        return "tiktok";
    if (host.includes("reddit.com"))
        return "reddit";
    if (host.includes("spotify.com") || host === "open.spotify.com")
        return "spotify";
    if (host.includes("linkedin.com"))
        return "linkedin";
    if (host.includes("github.com"))
        return "github";
    if (host.endsWith("nytimes.com") ||
        host.endsWith("wsj.com") ||
        host.endsWith("bloomberg.com") ||
        host.endsWith("ft.com") ||
        host.endsWith("theatlantic.com") ||
        host.endsWith("washingtonpost.com") ||
        host.endsWith("theguardian.com") ||
        host.endsWith("reuters.com") ||
        host.endsWith("apnews.com")) {
        return "news";
    }
    return "other";
}
export function queryLengthBucket(s) {
    const n = s.trim().length;
    if (n <= 8)
        return "xs";
    if (n <= 32)
        return "s";
    if (n <= 80)
        return "m";
    if (n <= 200)
        return "l";
    return "xl";
}
/**
 * Stable short hash for opaque identifiers — lets PostHog break events
 * down by collection / stack without leaking raw ids. djb2 truncated
 * to base36. Not cryptographic; just enough to keep the property
 * deterministic per id.
 */
export function hashId(id) {
    let h = 5381;
    for (let i = 0; i < id.length; i++) {
        h = ((h << 5) + h + id.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(36);
}
